import exemptionReasons from '../../steps/Assess/exemption-reasons';
import { Payload } from './types';
import * as Interfaces from 'interfaces';
import { parsePhoneNumber } from 'react-phone-number-input';
import {
  isExemptionApplication,
  isCrossBorderResident,
  isReturnExemption
} from 'steps/Assess/travel-reasons';
import { CovidAddress } from 'interfaces/common';

function serializeExemptionReasons(reasons) {
  return reasons
    .map((item, i) => ({ index: i, ...item }))
    .filter(item => item && item.selected)
    .map(({ index }) => exemptionReasons[index].value)
    .join('|');
}

export function getStreetAddress(fullAddress?: string): string {
  if (!fullAddress) return '';
  if (!fullAddress.includes(',')) return fullAddress;
  const strAddress: string[] = fullAddress.split(',');
  strAddress.splice(strAddress?.length - 1, 1);
  return strAddress.join(',');
}

export const prepare = (formValues: Interfaces.Common.FormValues): Payload => {
  const isReturn = isReturnExemption(formValues.assess.reasons);
  const isExemption = isReturn || isExemptionApplication(formValues.assess.reasons);
  const isCrossBorder = isCrossBorderResident(formValues.assess.reasons);
  const payload: Payload = {
    permitType: isExemption ? 'travel-exemption' : 'permit',
    firstName: formValues.identity.givenName,
    familyName: formValues.identity.familyName,
    'email-1': formValues.identity.email,
    phoneMobile: formatPhone(formValues.identity.phone),
    homeAddress: homeAddress(formValues),
    whoIsTravelling: whoIsTravelling(formValues),
    vaccinationStatus: formValues.assess.vaccinationStatus,
    travelReason: formValues.assess.reasons || '',
    otherTravelReason: formValues.assess.otherText,
    departureDate: formatDate(formValues.assess.Depart.date),
    leavingFrom: isCrossBorder ? homeAddress(formValues) : leavingFrom(formValues),
    goingTo: goingTo(formValues),
    exemptionReason: isExemption
      ? isReturn
        ? 'Returning home'
        : serializeExemptionReasons(formValues.assess.exemptionReason)
      : '',
    circumstances: isExemption
      ? isReturn
        ? formValues.assess.returningReason || ''
        : formValues.assess.circumstances || ''
      : '',
    negativeTest: isExemption ? formValues.assess.negativeTest || '' : '',
    ...getSupportingDocuments(formValues),
    quarantine: {
      fromQuarantine: formValues.assess.Depart.quarantine.fromQuarantine,
      hotel: formValues.assess.Depart.quarantine.hotel
    }
  };

  return payload;
};

const getSupportingDocuments = (formValues: Interfaces.Common.FormValues) => {
  if (
    !isExemptionApplication(formValues.assess.reasons) &&
    !isReturnExemption(formValues.assess.reasons)
  ) {
    return {};
  }

  if (
    formValues.assess.hasDriverLicence === 'yes' &&
    formValues.assess.SupportingDocuments?.driversLicence
  ) {
    return {
      supportingDocuments: {
        driversLicence: {
          ...formValues.assess.SupportingDocuments.driversLicence,
          dateOfBirth: reverseDate(formValues.assess.SupportingDocuments.driversLicence.dateOfBirth)
        }
      }
    };
  }

  return {};
};

const whoIsTravelling = (values: Interfaces.Common.FormValues) => {
  return values.identity.whoIsTravelling === 'Me'
    ? [`${values.identity.givenName} ${values.identity.familyName}`]
    : values.identity.whoIsTravelling === 'Me and my dependants or anyone under 12'
    ? [`${values.identity.givenName} ${values.identity.familyName}`].concat(
        values.identity.othersTravel.map(traveller => traveller.name)
      )
    : [];
};

const getOverseasAddressDetails = (address: CovidAddress | undefined) => {
  const isOverseas = address?.isOverseasAddress;

  if (isOverseas) {
    return {
      isOverseas,
      applicantId: '',
      addressId: '',
      row1: address?.overseas?.info.basic ?? '',
      row2: '',
      suburb: '',
      state: '',
      postcode: '',
      lga: '',
      country: address?.overseas.country ?? ''
    };
  }

  return {
    isOverseas,
    applicantId: '',
    addressId: address?.fullDetails.id ?? '',
    row1: getStreetAddress(address?.fullDetails.fullAddress),
    row2: '',
    suburb: address?.fullDetails.suburb ?? '',
    state: address?.fullDetails.state ?? '',
    postcode: address?.fullDetails.postCode ?? '',
    lga: address?.fullDetails.lga ?? '',
    country: 'Australia'
  };
};

const homeAddress = (values: Interfaces.Common.FormValues) => {
  if (values.assess.Depart.from === 'home') {
    return leavingFrom(values);
  }

  return getOverseasAddressDetails(values.assess.Depart.homeAddress);
};

const leavingFrom = (values: Interfaces.Common.FormValues) => {
  return getOverseasAddressDetails(values.assess.Depart.fromAddress);
};

const goingTo = (values: Interfaces.Common.FormValues) => {
  if (values.assess.Depart.homeAddressIsDestination) {
    const addresses = homeAddress(values);
    addresses.row2 = addresses.row1;
    addresses.row1 = 'Home';
    return addresses;
  }

  const addressDetails = getOverseasAddressDetails(values.assess.Depart.target);

  return {
    ...addressDetails,
    row1: values.assess.Depart.targetPlace,
    row2: addressDetails.row1
  };
};

const formatDate = (val: string, separator = '/') => val.replace(/-/g, separator);

const reverseDate = (val: string, separator = '/') => val.split('-').reverse().join(separator);

const formatPhone = (val: string) => parsePhoneNumber(val).number as string;
